/**
 * What the "QR Code" button should do for a given locale row.
 *
 * The QR encodes `https://api.ev-cuhk.net/open/epack/<id>`, which opens the
 * LOCALE in the app — and the app can only open a **published** one. An
 * unpublished epack (`is_ready = 0`) is filtered out server-side, so a student
 * who scans a draft's code is told the LOCALE cannot be found. That message is
 * indistinguishable from a broken QR, and it arrives on the student's phone, in
 * the field, long after the teacher could have done anything about it.
 *
 * So an unpublished LOCALE never gets a code handed out: the composer offers to
 * publish it first, and shows the code only once that write has landed.
 *
 * THE DECISION READS THE SAVED ROW, NEVER THE PUBLISH TOGGLE. The settings
 * panel's `localePublish` is the teacher's *unsaved intent*; a student's scan
 * can only ever see what was persisted. The two disagree exactly when the
 * teacher has flipped Publish and not yet pressed Update — the case where
 * trusting the toggle would print a code for a draft.
 *
 * `is_ready` arrives as a number from a freshly-built row and as a STRING from
 * the API (php_stringify), so it is always compared through parseInt.
 */

/** Action for the QR button: 'show', 'publish-first', or 'none'. */
export function qrCodeAction(locale) {
  if (!locale || locale.id === null || locale.id === undefined || locale.id === '') {
    // No locale in hand — the button should not have rendered; do nothing
    // rather than opening an empty dialog or writing to an undefined id.
    return 'none';
  }
  return isLocalePublished(locale) ? 'show' : 'publish-first';
}

/** True when the SAVED row is published, tolerating "1" / 1 / "0" / 0 / null. */
export function isLocalePublished(locale) {
  if (!locale) {
    return false;
  }
  return parseInt(locale.is_ready, 10) > 0;
}

/**
 * The generic-CRUD request that publishes one locale.
 *
 * Deliberately a MINIMAL row. The settings panel may be holding other unsaved
 * edits, and clicking "QR Code" is not consent to persist them — writing the
 * panel's full row here would quietly save every one of them. Partial rows are
 * the established pattern for this cmd (see ContentDetailView's payload-only
 * update).
 *
 * `update` on `epack` is gated server-side by `locale_write_allowed` (lapsed
 * subscription, ownership, tenant). A refusal comes back as the legacy `-1`
 * envelope, which API.isApplicationError routes to the fail callback — so the
 * caller must treat failure as "do not show the code".
 */
export function publishLocaleRequest(locale) {
  return {
    table: 'epack',
    where_field: 'id',
    where_value: locale.id,
    row: { is_ready: 1 },
  };
}
